import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Card, StatusPill } from '../../components/primitives/index.js'
import SEO from '../../components/SEO.jsx'
import { useCustomer } from '../../context/CustomerContext.jsx'
import { useProducts } from '../../context/ProductsContext.jsx'
import { customerApi } from '../../lib/customerApi.js'
import { fmtMoney } from '../../utils/invoiceUtils.js'
import { STATUS_META } from '../TrackOrderPage.jsx'
import AccountOrders from './AccountOrders.jsx'
import AccountAddresses from './AccountAddresses.jsx'
import AccountSettings from './AccountSettings.jsx'
import AccountRewards from './AccountRewards.jsx'
import AccountReferrals from './AccountReferrals.jsx'
import AccountWishlist from './AccountWishlist.jsx'
import './Account.css'

function initialsOf(name) {
  return (name ?? '')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

/** Big tier-colored hero — avatar ring, tier pill, and progress bar all key
 * off the same tier color so the tier reads as the dominant visual signal. */
function Hero({ customer, rewards }) {
  const tier = rewards?.tiersEnabled ? rewards?.tier : null
  const nextTier = rewards?.tiersEnabled ? rewards?.nextTier : null
  const memberSince = customer?.createdAt
    ? new Date(customer.createdAt).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : null

  return (
    <Card className="account-hero">
      <div className="account-hero__top">
        <div className="account-hero__avatar" style={tier ? { borderColor: tier.color, color: tier.color } : undefined}>
          {initialsOf(customer?.name) || '—'}
        </div>
        <div className="account-hero__id">
          <div className="account-hero__name-row">
            <h2>{customer?.name}</h2>
            {tier && (
              <span className="account-hero__tier-pill" style={{ color: tier.color, background: `${tier.color}1a`, borderColor: `${tier.color}55` }}>
                {tier.name}
              </span>
            )}
          </div>
          <span className="account-hero__meta">
            {customer?.email}
            {memberSince && <> &middot; Member since {memberSince}</>}
          </span>
        </div>
        <div className="account-hero__points">
          <span className="account-hero__points-value">{rewards?.balance ?? rewards?.points ?? 0}</span>
          <span className="account-hero__points-label">{rewards?.pointsName ?? 'Protocol Points'}</span>
        </div>
      </div>

      {nextTier && (
        <div className="account-hero__progress">
          <span className="account-hero__progress-label">
            {fmtMoney(nextTier.remaining)} more unlocks <strong>{nextTier.name}</strong>
          </span>
        </div>
      )}
    </Card>
  )
}

function Overview({ customer, orders, ordersLoading, rewards, setTab }) {
  const lifetimeSpend = rewards?.lifetimeSpend ?? customer?.lifetimeSpend ?? 0

  return (
    <div className="account-overview">
      {!customer?.emailVerified && (
        <Card className="account-overview__notice">
          <p>Confirm your email to unlock your full order history and rewards.</p>
        </Card>
      )}

      <Hero customer={customer} rewards={rewards} />

      <div className="account-overview__stats">
        <Card className="account-overview__stat account-overview__stat--blue">
          <span className="account-overview__stat-label">Lifetime orders</span>
          <span className="account-overview__stat-value">{orders?.length ?? 0}</span>
        </Card>
        <Card className="account-overview__stat account-overview__stat--green">
          <span className="account-overview__stat-label">Lifetime spend</span>
          <span className="account-overview__stat-value">{fmtMoney(lifetimeSpend)}</span>
        </Card>
        <Card className="account-overview__stat account-overview__stat--yellow">
          <span className="account-overview__stat-label">Reward points</span>
          <span className="account-overview__stat-value">{rewards?.balance ?? '—'}</span>
        </Card>
        <Card className="account-overview__stat account-overview__stat--purple">
          <span className="account-overview__stat-label">Store credit</span>
          <span className="account-overview__stat-value">{fmtMoney(customer?.storeCredit)}</span>
        </Card>
      </div>

      <div className="account-overview__section">
        <div className="account-overview__section-head">
          <h3>Recent orders</h3>
          <button className="account-overview__link" onClick={() => setTab('orders')}>
            View all &rarr;
          </button>
        </div>
        {ordersLoading && <p className="account-overview__empty">Loading&hellip;</p>}
        {!ordersLoading && orders.length === 0 && <p className="account-overview__empty">No orders yet.</p>}
        {!ordersLoading &&
          orders.slice(0, 3).map((order) => {
            const meta = STATUS_META[order.status] ?? { label: order.status, color: '#a3a3a3' }
            return (
              <Card key={order.invoiceNumber} className="account-overview__order" style={{ '--status-color': meta.color }}>
                <span className="account-overview__order-invoice">{order.invoiceNumber}</span>
                <StatusPill label={meta.label} color={meta.color} />
                <span className="account-overview__order-total">{fmtMoney(order.total)}</span>
              </Card>
            )
          })}
      </div>
    </div>
  )
}

/**
 * Single /account route, tabs via ?tab= query param — mirrors Prod's
 * AccountPage.jsx exactly (useSearchParams, not nested routes), so the
 * eventual merge into Prod is a straight drop-in.
 */
export default function AccountPage() {
  const { customer, impersonatedBy, loading, logout } = useCustomer()
  const { customerAccounts } = useProducts()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const [orders, setOrders] = useState([])
  const [ordersLoading, setOrdersLoading] = useState(true)
  const [rewards, setRewards] = useState(null)

  const features = customerAccounts?.features ?? {}

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'orders', label: 'Orders' },
    ...(features.rewards !== false ? [{ id: 'rewards', label: 'Rewards' }] : []),
    ...(features.referrals !== false && customer?.referralsEnabled ? [{ id: 'referrals', label: 'Referrals' }] : []),
    ...(features.wishlist !== false ? [{ id: 'wishlist', label: 'Wishlist' }] : []),
    { id: 'addresses', label: 'Addresses' },
    { id: 'settings', label: 'Settings' },
  ]

  const tab = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'overview'
  const setTab = (id) => setParams(id === 'overview' ? {} : { tab: id }, { replace: true })

  useEffect(() => {
    if (customerAccounts?.enabled === false) { navigate('/', { replace: true }); return }
    if (!loading && !customer) navigate('/account/login', { replace: true })
  }, [loading, customer, navigate, customerAccounts])

  useEffect(() => {
    if (!customer) return
    setOrdersLoading(true)
    customerApi.myOrders().then((res) => {
      setOrders(res?.orders ?? [])
      setOrdersLoading(false)
    })
    if (features.rewards !== false) customerApi.getRewards().then(setRewards)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer])

  async function handleLogout() {
    await logout()
    navigate('/')
  }

  if (loading || !customer) {
    return (
      <div className="container account-page">
        <p className="account-page__loading">Loading your account&hellip;</p>
      </div>
    )
  }

  return (
    <div className="container account-page">
      <SEO config={{ title: 'My Account', robots: 'noindex, nofollow', titleRaw: true }} />

      {impersonatedBy && (
        <div className="account-page__impersonation-banner">
          Staff session — viewing as {customer.name || customer.email} on behalf of {impersonatedBy}.
          Ends automatically in 20 minutes.{' '}
          <button type="button" onClick={handleLogout}>End session now</button>
        </div>
      )}

      <div className="account-page__header">
        <div>
          <span className="account-page__eyebrow">Welcome back</span>
          <h1>{customer.name}</h1>
        </div>
        <Button variant="ghost" onClick={handleLogout}>
          Sign out
        </Button>
      </div>

      <div className="account-page__tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`account-page__tab ${tab === t.id ? 'is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="account-page__content">
        {tab === 'overview' && <Overview customer={customer} orders={orders} ordersLoading={ordersLoading} rewards={rewards} setTab={setTab} />}
        {tab === 'orders' && <AccountOrders orders={orders} loading={ordersLoading} customer={customer} />}
        {tab === 'addresses' && <AccountAddresses />}
        {tab === 'settings' && <AccountSettings />}
        {tab === 'rewards' && <AccountRewards />}
        {tab === 'referrals' && <AccountReferrals />}
        {tab === 'wishlist' && <AccountWishlist />}
      </div>
    </div>
  )
}
